import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { db, storage } from '../../lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { DesktopService } from '../../services/desktopService';
import AntigravityControl from './AntigravityControl';
import LiveInsightControl from './LiveInsightControl';
import ITermControl from './ITermControl';
import TerminalControl from './TerminalControl';
import useChatStore from '../../store/useChatStore';
import useAuthStore from '../../store/useAuthStore';
import RichMessageRenderer from './RichMessageRenderer';

const CloseIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
const Spinner = () => <svg className="animate-spin h-8 w-8 text-[var(--accent-primary)]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;
const BotIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;

const ControlOverlay = ({ open, onClose, mode, targetDeviceId, targetUid, folderPath, nodeId, deviceData }) => {
    const [displaySrc, setDisplaySrc] = useState(null);
    const [error, setError] = useState(null);
    const [isConnecting, setIsConnecting] = useState(true);
    const [isLiveMode, setIsLiveMode] = useState(true);

    const { messages, activeChat, isAiThinking } = useChatStore();
    const { user } = useAuthStore();
    const chatContainerRef = useRef(null);

    // Auto scroll chat
    useEffect(() => {
        if (!isLiveMode && chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLiveMode]);

    // Start/Stop Stream
    useEffect(() => {
        if (!open || !targetUid || !targetDeviceId) return;

        setIsConnecting(true);
        setError(null);

        const startStreamAndApp = async () => {
            try {
                // 1. Start continuous screen streaming (Only for Antigravity & VS Code)
                if (mode !== 'iterm') {
                    await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, { command: 'start_stream' });
                }

                // 2. Open project in Antigravity or VS Code mode
                if ((mode === 'antigravity' || mode === 'vscode') && folderPath) {
                    await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                        command: 'open_project',
                        path: folderPath,
                        target_ide: mode
                    });
                } else if (mode === 'iterm') {
                    // Send an empty command to ensure iTerm is focused/created
                    await DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                        command: 'run_iterm_control',
                        text: '',
                        nodeId: nodeId,
                        folderPath: folderPath
                    });
                }
                setIsConnecting(false);
            } catch (err) {
                console.error(err);
                setError("Failed to connect to Desktop Engine.");
                setIsConnecting(false);
            }
        };

        startStreamAndApp();

        return () => {
            if (targetUid && targetDeviceId) {
                DesktopService.sendDesktopCommand(targetUid, targetDeviceId, { command: 'stop_stream' }).catch(console.error);
            }
        };
    }, [open, targetUid, targetDeviceId, mode, folderPath]);

    const [hasFrame, setHasFrame] = useState(false);

    // 1. Fetch Stream Frames whenever confirmed in meta
    useEffect(() => {
        if (!open || !targetUid || !targetDeviceId) return;

        const deviceRef = doc(db, 'users', targetUid, 'devices', targetDeviceId);
        const path = `stream/${targetUid}/${targetDeviceId}/latest.jpg`;
        const storageRef = ref(storage, path);

        const refreshFrame = async () => {
            if (!isLiveMode) return;
            try {
                const url = await getDownloadURL(storageRef);
                const separator = url.includes('?') ? '&' : '?';
                setDisplaySrc(`${url}${separator}t=${Date.now()}`);
                setHasFrame(true);
            } catch (err) { }
        };

        const unsub = onSnapshot(deviceRef, (docSnap) => {
            if (docSnap.exists() && docSnap.data().last_frame_at) {
                refreshFrame();
            }
        });

        // Fallback for initial discovery
        const timer = setTimeout(() => {
            if (!displaySrc) refreshFrame();
        }, 3000);

        return () => {
            unsub();
            clearTimeout(timer);
        };
    }, [open, targetUid, targetDeviceId, isLiveMode]);

    if (!open) return null;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-4 z-[50] bg-[var(--bg-main)] rounded-3xl border border-[var(--border-strong)] shadow-2xl overflow-hidden flex flex-col"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)] shrink-0">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-3">
                                <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${mode === 'iterm' ? 'bg-green-500/10 text-green-500' : mode === 'vscode' ? 'bg-blue-500/10 text-blue-500' : 'bg-[var(--accent-primary)]/10 text-[var(--accent-primary)]'}`}>
                                    {mode === 'antigravity' ? (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
                                    ) : mode === 'vscode' ? (
                                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.15 2.587L18.21.21a1.494 1.494 0 0 0-1.705.29l-9.46 8.63-4.12-3.128a.999.999 0 0 0-1.276.057L.327 7.261A1 1 0 0 0 .326 8.74L8.499 15.3l-8.173 6.56a1 1 0 0 0 .001 1.479l1.322 1.202a1 1 0 0 0 1.276.057l4.12-3.128 9.46 8.63a1.492 1.492 0 0 0 1.704.29l4.942-2.377A1.5 1.5 0 0 0 24 21.413V2.587a1.5 1.5 0 0 0-.85-1.37zm-6.07 14.821L11.51 12.5l5.57-4.908v9.816z" /></svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-[var(--text-main)]">
                                        {mode === 'antigravity' ? 'Antigravity Control Mode' :
                                            mode === 'vscode' ? 'VS Code Control Mode' :
                                                mode === 'iterm' ? 'iTerm Control Mode' :
                                                    mode === 'live_insight' ? 'Live Insight Mode' :
                                                        'Terminal Control Mode'}
                                    </h3>
                                    <p className="text-[10px] uppercase tracking-widest text-[var(--text-dim)] font-medium mt-0.5">
                                        {targetDeviceId}
                                    </p>
                                </div>
                            </div>

                            <div className="h-6 w-[1px] bg-[var(--border-subtle)] mx-2" />

                            {/* View Toggle */}
                            <div className="flex bg-[var(--bg-tertiary)] p-1 rounded-lg border border-[var(--border-subtle)]">
                                <button
                                    onClick={() => setIsLiveMode(true)}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${isLiveMode ? 'bg-[var(--accent-primary)] text-[var(--bg-main)] shadow-sm' : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'}`}
                                >
                                    Live Stream
                                </button>
                                <button
                                    onClick={() => setIsLiveMode(false)}
                                    className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${!isLiveMode ? 'bg-[var(--accent-primary)] text-[var(--bg-main)] shadow-sm' : 'text-[var(--text-muted)] hover:text-[var(--text-main)]'}`}
                                >
                                    Chat History
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-tertiary)] transition-colors"
                        >
                            <CloseIcon className="w-5 h-5" />
                        </button>
                    </div>

                    {/* View Area (Top) */}
                    <div className="flex-1 bg-black relative flex items-center justify-center overflow-hidden min-h-[40%]">
                        {isLiveMode ? (
                            <>
                                {error ? (
                                    <div className="text-red-400 font-medium p-4 bg-red-400/10 rounded-xl border border-red-400/20">{error}</div>
                                ) : displaySrc ? (
                                    <img
                                        src={displaySrc}
                                        alt="Live Desktop"
                                        className="w-full h-full object-contain cursor-crosshair"
                                        onClick={(e) => {
                                            const rect = e.target.getBoundingClientRect();
                                            const x = (e.clientX - rect.left) / rect.width;
                                            const y = (e.clientY - rect.top) / rect.height;

                                            // Send normalized coordinates to desktop agent
                                            DesktopService.sendDesktopCommand(targetUid, targetDeviceId, {
                                                command: 'click_remote',
                                                x,
                                                y
                                            });
                                        }}
                                    />
                                ) : (
                                    <div className="flex flex-col items-center justify-center gap-4">
                                        <Spinner />
                                        <p className="text-[var(--text-muted)] text-sm font-medium tracking-wide animate-pulse">
                                            {isConnecting ? "Connecting to Engine..." : "Waiting for Stream..."}
                                        </p>
                                    </div>
                                )}
                                {displaySrc && (
                                    <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10">
                                        <div className="w-2 h-2 rounded-full bg-[var(--accent-primary)] shadow-[0_0_8px_var(--accent-primary)] animate-pulse" />
                                        <span className="text-[10px] font-bold text-white tracking-widest opacity-90">LIVE VIEW</span>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div ref={chatContainerRef} className="absolute inset-0 bg-[var(--bg-main)] overflow-y-auto p-6 custom-scrollbar flex flex-col gap-6">
                                {messages?.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full opacity-40">
                                        <div className="w-16 h-16 rounded-3xl bg-[var(--bg-tertiary)] border border-[var(--border-strong)] flex items-center justify-center mb-4">
                                            <BotIcon className="w-8 h-8 text-[var(--text-muted)]" />
                                        </div>
                                        <p className="text-sm font-medium tracking-tight">No history yet in this control session.</p>
                                    </div>
                                ) : (
                                    messages?.map((msg) => {
                                        const isMe = msg.senderId === user?.uid;
                                        return (
                                            <div key={msg.id} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                                                <div className="flex max-w-[85%] items-end gap-3 group">
                                                    {!isMe && (
                                                        <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 mb-[18px] bg-[var(--bg-tertiary)] shadow-md border border-[var(--border-subtle)] flex items-center justify-center">
                                                            <BotIcon className="w-4 h-4 text-indigo-400" />
                                                        </div>
                                                    )}
                                                    <div className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} min-w-0`}>
                                                        <div className={`px-4 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed break-words ${isMe ? 'bg-indigo-600 text-white rounded-br-sm' : 'bg-[var(--bg-secondary)] text-[var(--text-main)] border border-[var(--border-subtle)] rounded-bl-sm'}`}>
                                                            <RichMessageRenderer message={msg} isMe={isMe} chatId={activeChat?.id} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                {(isAiThinking || activeChat?.isAgentProcessing) && (
                                    <div className="flex w-full justify-start animate-in fade-in duration-300">
                                        <div className="flex max-w-[85%] items-end gap-3 group">
                                            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 bg-[var(--bg-tertiary)] shadow-md border border-[var(--border-subtle)] flex items-center justify-center">
                                                <BotIcon className="w-4 h-4 text-indigo-400" />
                                            </div>
                                            <div className="flex flex-col items-start min-w-0">
                                                <div className="px-4 py-2.5 rounded-2xl shadow-sm text-sm bg-[var(--bg-secondary)] text-[var(--text-main)] border border-[var(--border-subtle)] rounded-bl-sm flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 bg-[var(--text-muted)] rounded-full animate-bounce"></span>
                                                    <span className="w-1.5 h-1.5 bg-[var(--text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                                                    <span className="w-1.5 h-1.5 bg-[var(--text-muted)] rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Controller Area (Bottom) */}
                    <div className="shrink-0 bg-[var(--bg-secondary)] border-t border-[var(--border-strong)] z-10 shadow-[0_-10px_40px_rgba(0,0,0,0.1)]">
                        {(mode === 'antigravity' || mode === 'vscode') && (
                            <AntigravityControl
                                targetUid={targetUid}
                                targetDeviceId={targetDeviceId}
                                nodeId={nodeId}
                                mode={mode}
                                onCommandSent={() => {
                                    // Force back to history to see the command being processed
                                    setIsLiveMode(false);
                                }}
                            />
                        )}
                        {mode === 'live_insight' && (
                            <LiveInsightControl
                                targetUid={targetUid}
                                targetDeviceId={targetDeviceId}
                                onCommandSent={() => {
                                    // Maybe stay on live stream here or refresh
                                }}
                            />
                        )}
                        {mode === 'terminal' && (
                            <TerminalControl
                                targetUid={targetUid}
                                targetDeviceId={targetDeviceId}
                                folderPath={folderPath}
                                nodeId={nodeId}
                            />
                        )}
                        {mode === 'iterm' && (
                            <ITermControl
                                targetUid={targetUid}
                                targetDeviceId={targetDeviceId}
                                nodeId={nodeId}
                                folderPath={folderPath}
                                deviceData={deviceData}
                            />
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ControlOverlay;
